import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import AutocompleteArrayInputEx from "components/_extensions/react-admin/ra-ui-materialui/src/input/AutocompleteArrayInputEx";
import { NumberWithSuffixInput } from "components/inputs/NumberWithSuffixInput";
import {
	CustomerCCTOCDChangeType,
	CustomerCDServiceChargeType,
	CustomerCDStorageChargeType,
} from "entities/CustomerCDChargeType";
import { MapperType } from "entities/MapperType";
import { SplitOrdersType } from "entities/SplitOrdersType";
import React, { useEffect } from "react";
import {
	BooleanInput,
	ReferenceArrayInput,
	ReferenceInput,
	SelectInput,
	required,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { GridForm, GridInput } from "../../components/GridForm";
import { EmailInput, NumberInputEx, YesNoInput } from "../../components/inputs/";
import LanguageSelect from "../../components/inputs/LanguageSelect";
import { SelectEnumInput } from "../../components/inputs/SelectEnumInput";
import { AppManagerRoles, AppRoles, OrderService, OrderServices } from "../../entities";
import { CompanyCustomerType } from "../../entities";
import { Currencies } from "../../entities/Currencies";
import { CommodityInput } from "../../resources/orders/forms/inputs/CommodityInput";
import { isInArray } from "../../utils/general";
import { CardBlock } from "./CardBlock";
import { UpdateForm } from "./UpdateForm";

const CompanySettingsForm = (props) => {
	const { permissions } = usePermissions();
	const { values } = useFormState();
	const form = useForm();
	const translate = useTranslate();

	const filter = props.currentCompanyId ? { "id:ne": props.currentCompanyId } : {};
	const isAppManager = permissions && isInArray(AppRoles, permissions.role);
	const isTransporter = values["type"] == CompanyCustomerType.transporter;
	const onSplitOrderChange = (e) => {
		if (e.target.value !== SplitOrdersType.not_split) {
			form.change("is_order_import_add_loading_order_to_ref", false);
		} else {
			form.change("is_order_import_split_document_content", false);
		}
	};
	const onAttachSourceDocChange = (e) => {
		if (!e.target.value) {
			form.change("is_order_import_split_document_content", false);
		}
	};

	return (
		<GridForm {...props}>
			<GridInput md={3} sm={6} component={LanguageSelect} />
			<GridInput
				lg={1}
				md={2}
				sm={6}
				component={SelectEnumInput}
				enumObj={Currencies}
				translate={false}
				enumName="currency"
				source="currency"
				disabled={!isAppManager}
			/>
			<GridInput md={3} sm={6} component={EmailInput} source="deviation_notification_email" />
			{!isTransporter && (
				<GridInput md={1} sm={6} component={YesNoInput} source="plumb_seal" defaultValue={false} />
			)}
			{!isTransporter && <GridInput md={3} sm={6} component={CommodityInput} source="commodity" />}
			{/* <GridInput md={3} sm={6}>
				<ReferenceInput source="area_id" reference="areas" filter={byTerminalFilter}>
					<SelectInput optionText="name" />
				</ReferenceInput>
			</GridInput> */}
			<GridInput md={4} sm={6} options={{ size: "small" }}>
				<ReferenceArrayInput
					source="preferred_transporters"
					reference="transporters"
					filter={{ ...filter, no_range: true }}
					sort={{ field: "full_name", order: "ASC" }}
				>
					{/* Choices are shaking, the solution was taken from link below */}
					{/* https://github.com/marmelab/react-admin/issues/5926 */}
					<AutocompleteArrayInputEx
						options={{
							suggestionsContainerProps: {
								modifiers: {
									computeStyle: {
										enabled: true,
										gpuAcceleration: false,
									},
									applyStyle: {
										enabled: true,
									},
								},
							},
						}}
						optionText="full_name"
					/>
				</ReferenceArrayInput>
			</GridInput>
			{!isTransporter && (
				<GridInput md={1} sm={6}>
					<ReferenceInput source="terminal_id" reference="terminal">
						<SelectInput optionText="name" />
					</ReferenceInput>
				</GridInput>
			)}
			{!isTransporter && (
				<GridInput
					md={1}
					sm={6}
					component={YesNoInput}
					source="is_output_document"
					defaultValue={false}
				/>
			)}
			<GridInput md={1} sm={6}>
				<ReferenceInput
					fullWidth
					reference="mappers"
					source="order_import_mapper_id"
					label={translate("osfc.dialogs.orders-import-upload.fields.mapper_id")}
					filter={{
						type: MapperType.order_import,
						company_id: props.currentCompanyId,
						is_upload_order: true,
					}}
					allowEmpty
				>
					<SelectInput name="name" />
				</ReferenceInput>
			</GridInput>
			<GridInput md={3} sm={6}>
				<BooleanInput label="Copy docs" source="copy_docs_on_clone" />
			</GridInput>
		</GridForm>
	);
};

const CompanySettingsBlockBase = ({ component: Component, ...props }) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();

	const isAdminAppManager = permissions && isInArray(AppRoles, permissions.role);

	const currentCompanyId = permissions?.company_id || (props?.record && props.record.id);

	return currentCompanyId ? (
		<UpdateForm
			id={currentCompanyId}
			resource="customer-companies"
			render={({ body, buttons }) => (
				<CardBlock
					Component={isAdminAppManager ? Box : Card}
					title={isAdminAppManager ? "" : translate("tab.company_settings.name")}
					buttons={buttons}
					children={body}
				/>
			)}
		>
			<Component currentCompanyId={currentCompanyId} />
		</UpdateForm>
	) : (
		<></>
	);
};

const CompanyServiceSettingsForm = (props) => {
	const { permissions } = usePermissions();
	const { values } = useFormState();
	const form = useForm();
	const t = useTranslate();

	const isAppManager = permissions && isInArray(AppManagerRoles, permissions.role);

	useEffect(() => {
		if (values["cd_storage_charge_type"] !== CustomerCDStorageChargeType.hours) {
			form.change("cd_storage_charge_hours", 0);
		}
		if (values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.minutes) {
			form.change("cc_to_cd_minutes", 30);
		}
	}, [values["cd_storage_charge_type"], values["cc_to_cd_change_type"]]);

	return (
		<GridForm {...props}>
			{(isAppManager ||
				(!isAppManager && values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.none)) && (
				<GridInput
					xs={12}
					sm={values["cc_to_cd_change_type"] == CustomerCCTOCDChangeType.minutes ? 6 : 12}
					source="cc_to_cd_change_type"
					component={SelectEnumInput}
					disabled={!isAppManager}
					enumObj={CustomerCCTOCDChangeType}
					enumName="customer_cc_to_cd_change"
					validate={[required()]}
					label={t("resources.customer-companies.fields.cc_to_cd_change_type", {
						cc_service_name: OrderServices[OrderService.reloadCarCar].full_name,
						cd_service_name: OrderServices[OrderService.crossDock].full_name,
					})}
				/>
			)}
			{(isAppManager ||
				(!isAppManager && values["cc_to_cd_change_type"] !== CustomerCCTOCDChangeType.none)) &&
				values["cc_to_cd_change_type"] == CustomerCCTOCDChangeType.minutes && (
					<GridInput
						xs={12}
						sm={6}
						source="cc_to_cd_minutes"
						component={NumberWithSuffixInput}
						disabled={!isAppManager}
						suffix="minutes"
						min={0}
						validate={[required()]}
					/>
				)}
			<GridInput
				xs={12}
				source="cd_service_charge_type"
				component={SelectEnumInput}
				disabled={!isAppManager}
				enumObj={CustomerCDServiceChargeType}
				enumName="customer_cd_service_charge"
				validate={[required()]}
				label={t("resources.customer-companies.fields.cd_service_charge_type", {
					cd_service_name: OrderServices[OrderService.crossDock].full_name,
				})}
			/>
			<GridInput
				xs={12}
				sm={values["cd_storage_charge_type"] == CustomerCDStorageChargeType.hours ? 6 : 12}
				source="cd_storage_charge_type"
				component={SelectEnumInput}
				disabled={!isAppManager}
				enumObj={CustomerCDStorageChargeType}
				enumName="customer_cd_storage_charge"
				validate={[required()]}
				label={t("resources.customer-companies.fields.cd_storage_charge_type", {
					cd_service_name: OrderServices[OrderService.crossDock].full_name,
				})}
			/>
			{values["cd_storage_charge_type"] == CustomerCDStorageChargeType.hours && (
				<GridInput
					xs={12}
					sm={6}
					source="cd_storage_charge_hours"
					component={NumberWithSuffixInput}
					disabled={!isAppManager}
					suffix="hours"
					min={0}
					validate={[required()]}
				/>
			)}
		</GridForm>
	);
};

export const CompanySettingsBlock = (props) => (
	<CompanySettingsBlockBase component={CompanySettingsForm} {...props} />
);
export const CompanyServiceSettingsBlock = (props) => (
	<CompanySettingsBlockBase component={CompanyServiceSettingsForm} {...props} />
);
